import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import DashboardLayout from "./layouts/dashboard";
import PublicLayout from "./layouts/PublicLayout";
import AppLoader from "./components/GeneralComponents/AppLoader";
import LandingContent from "./components/portal/LandingContent";

const AssociatesList = lazy(() => import("./pages/Associates/AssociatesList"));
const AssociateDomainsList = lazy(() => import("./pages/Associates/AssociateDomainsList"));
const Chatting = lazy(() => import("./pages/Chatting/Chatting"));
const ClientsList = lazy(() => import("./pages/Clients/ClientsList"));
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const HolyQuranIndexes = lazy(
  () => import("./pages/LessonBooks/HolyQuranIndexes"),
);
const HolyQuranIndexMainPage = lazy(
  () => import("./pages/LessonBooks/HolyQuranIndexMainPage"),
);
const HolyQuranIndexPages = lazy(
  () => import("./pages/LessonBooks/HolyQuranIndexPages"),
);
const Gallery = lazy(() => import("./pages/LessonBooks/Gallery"));
const LoginGate = lazy(() => import("./pages/Login/Login"));
const PersonalNotes = lazy(() => import("./pages/PersonalNotes/PersonalNotes"));
const PersonalNotesByUser = lazy(
  () => import("./pages/PersonalNotes/PersonalNotesByUser"),
);
const QuidaEnglishIndexMainPage = lazy(
  () => import("./pages/LessonBooks/QuidaEnglishIndexMainPage"),
);
const QuidaEnglishIndexPages = lazy(
  () => import("./pages/LessonBooks/QuidaEnglishIndexPages"),
);
const StudentReports = lazy(
  () => import("./pages/StudentReports/StudentReports"),
);
const StudentsList = lazy(() => import("./pages/Students/StudentsList"));
const StudentsListByTeacher = lazy(
  () => import("./pages/Teachers/StudentsListByTeacher"),
);
const TeachersList = lazy(() => import("./pages/Teachers/TeachersList"));
const ToDoLists = lazy(() => import("./pages/ToDoList/ToDoLists"));
const ToDoListsByUser = lazy(() => import("./pages/ToDoList/ToDoListsByUser"));
const TrainingLesson = lazy(
  () => import("./pages/TrainingLesson/TrainingLesson"),
);
const TransactionsList = lazy(
  () => import("./pages/Transactions/TransactionsList"),
);
const UsersList = lazy(() => import("./pages/Users/UsersList"));
const UserDevices = lazy(() => import("./pages/UserDevices/UserDevices"));
const ActivityLogs = lazy(() => import("./pages/ActivityLogs/ActivityLogs"));
const Complaints = lazy(() => import("./pages/Complaints"));
const FeedbackMechanism = lazy(() => import("./pages/FeedbackMechanism"));
const Challenges = lazy(() => import("./pages/Challenges"));
const PublicHolidays = lazy(() => import("./pages/PublicHolidays"));
const OpenAI = lazy(() => import("./pages/OpenAI/OpenAI"));
const Feeds = lazy(() => import("./pages/Feeds/Feeds"));
const IslamicTopics = lazy(() => import("./pages/IslamicTopics/IslamicTopics"));
const Classes = lazy(() => import("./pages/Chatting/Classes"));
const Subjects = lazy(() => import("./pages/Chatting/Subjects"));
const Lessons = lazy(() => import("./pages/Chatting/Lessons"));
const LessonDetail = lazy(() => import("./pages/Chatting/LessonDetail"));
const ManageClassesByTeacher = lazy(
  () => import("./pages/Teachers/ManageClassesByTeacher"),
);

const ManageSubjectsByTeacher = lazy(
  () => import("./pages/Teachers/ManageSubjectsByTeacher"),
);

const ManageSubjectsByStudent = lazy(
  () => import("./pages/Students/ManageSubjectsByStudent"),
);

const ManageClassesByStudent = lazy(
  () => import("./pages/Students/ManageClassesByStudent"),
);
const ManageTeachersByStudent = lazy(
  () => import("./pages/Students/ManageTeachersByStudent"),
);
const CertificatesOverview = lazy(
  () => import("./pages/Certificates/CertificatesOverview"),
);
const NotFound = lazy(() => import("./pages/NotFound"));

function RouteFallback() {
  return <AppLoader minHeight="40vh" size="medium" />;
}

export default function Router() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<DashboardLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/admin-users" element={<UsersList />} />
          <Route path="/lesson-books" element={<Gallery />} />
          <Route
            path="/lesson-books/quran-pak"
            element={<HolyQuranIndexes />}
          />
          <Route
            path="/lesson-books/quran-pak/:index"
            element={<HolyQuranIndexPages />}
          />
          <Route
            path="/lesson-books/quran-pak/:index/:page_index"
            element={<HolyQuranIndexMainPage />}
          />
          <Route
            path="/lesson-books/quida-english"
            element={<QuidaEnglishIndexPages />}
          />
          <Route
            path="/lesson-books/quida-english/:page_index"
            element={<QuidaEnglishIndexMainPage />}
          />
          <Route path="/students" element={<StudentsList />} />
          <Route path="/OpenAI" element={<OpenAI />} />
          <Route
            path="/students/personal-notes/:user_id"
            element={<PersonalNotesByUser user_type="student" />}
          />
          <Route
            path="/students/user-devices/:user_id"
            element={<UserDevices user_type="student" />}
          />
          <Route
            path="/students/to-do-lists/:user_id"
            element={<ToDoListsByUser user_type="student" />}
          />
          <Route path="/training-lesson" element={<TrainingLesson />} />
          <Route path="/teachers" element={<TeachersList />} />
          <Route
            path="/teachers/personal-notes/:user_id"
            element={<PersonalNotesByUser user_type="teacher" />}
          />
          <Route
            path="/teachers/user-devices/:user_id"
            element={<UserDevices user_type="teacher" />}
          />
          <Route
            path="/teachers/students-list/:teacher_id"
            element={<StudentsListByTeacher />}
          />
          <Route
            path="/teachers/to-do-lists/:user_id"
            element={<ToDoListsByUser user_type="teacher" />}
          />
          <Route path="/associates" element={<AssociatesList />} />
          <Route path="/associates/:associate_id/classes" element={<Classes />} />
          <Route path="/associates/:associate_id/domains" element={<AssociateDomainsList />} />
          <Route
            path="/associates/personal-notes/:user_id"
            element={<PersonalNotesByUser user_type="associate" />}
          />
          <Route
            path="/associates/user-devices/:user_id"
            element={<UserDevices user_type="associate" />}
          />
          <Route
            path="/associates/to-do-lists/:user_id"
            element={<ToDoListsByUser user_type="associate" />}
          />
          <Route path="/clients" element={<ClientsList />} />
          <Route
            path="/clients/personal-notes/:user_id"
            element={<PersonalNotesByUser user_type="client" />}
          />
          <Route
            path="/clients/user-devices/:user_id"
            element={<UserDevices user_type="client" />}
          />
          <Route
            path="/clients/to-do-lists/:user_id"
            element={<ToDoListsByUser user_type="client" />}
          />
          <Route path="/class-room" element={<Chatting />} />
          <Route path="/class-room/:conversation_id" element={<Chatting />} />
          <Route path="/classes" element={<Navigate to="/associates" replace />} />
          <Route path="/subjects" element={<Subjects />} />
          <Route path="/subjects/:subjectId/lessons" element={<Lessons />} />
          <Route
            path="/subjects/:subjectId/lessons/:lessonId"
            element={<LessonDetail />}
          />
          <Route path="/personal-notes" element={<PersonalNotes />} />
          <Route path="/to-do-lists" element={<ToDoLists />} />
          <Route path="/transactions" element={<TransactionsList />} />
          <Route path="/student-reports" element={<StudentReports />} />
          <Route
            path="/teachers/manage-subjects-access/:teacher_id"
            element={<ManageSubjectsByTeacher />}
          />
          <Route
            path="/teachers/manage-classes-access/:teacher_id"
            element={<ManageClassesByTeacher />}
          />
          <Route
            path="/students/manage-subjects-access/:student_id"
            element={<ManageSubjectsByStudent />}
          />
          <Route
            path="/students/manage-classes-access/:student_id"
            element={<ManageClassesByStudent />}
          />
          <Route
            path="/students/manage-teachers-access/:student_id"
            element={<ManageTeachersByStudent />}
          />
          <Route path="/feeds" element={<Feeds />} />
          <Route path="/feeds-list" element={<Feeds />} />
          <Route path="/complaints" element={<Complaints />} />
          <Route path="/FeedbackMechanism" element={<FeedbackMechanism />} />
          <Route path="/Challenges" element={<Challenges />} />
          <Route path="/PublicHolidays" element={<PublicHolidays />} />
          <Route path="/activity-logs" element={<ActivityLogs />} />
          <Route path="/islamic-topics" element={<IslamicTopics />} />
          <Route path="/certificates" element={<CertificatesOverview />} />
        </Route>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingContent />} />
          <Route path="/login" element={<LoginGate />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
